import React, { useMemo, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import DashboardLayout from '../layouts/DashboardLayout'
import { Wallet, Clock, ChevronRight, FileText, Inbox } from 'lucide-react'

// "Finance applications" tab (Paul 2026-09-30) — the lender's (Expo Finance) review queue inside Ivory.
// The server confines a lender login to this tab (ApplicationController#enforce_lender_scope!).

export const STATUS_STYLE = {
  submitted: 'bg-brand-primary text-white border-brand-primary',
  with_lender: 'bg-brand-accent text-brand-primary-dark border-brand-primary/30',
  info_requested: 'bg-amber-50 text-amber-800 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  signed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ready: 'bg-emerald-600 text-white border-emerald-600',
  declined: 'bg-red-50 text-red-700 border-red-200',
  withdrawn: 'bg-brand-surface text-brand-muted border-brand-border',
}

export function waited(mins) {
  if (mins == null) return null
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  if (h < 24) return `${h}h ${mins % 60}m`
  return `${Math.floor(h / 24)}d ${h % 24}h`
}

const when = (iso) => (iso ? new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—')

export default function FinanceReview({ lender = {}, applications = [], counts = {}, is_admin }) {
  const [tab, setTab] = useState(counts.needs_action ? 'action' : 'all')
  const list = useMemo(() => {
    if (tab === 'action') return applications.filter((a) => ['submitted', 'with_lender'].includes(a.status))
    if (tab === 'patient') return applications.filter((a) => a.status === 'info_requested')
    return applications
  }, [tab, applications])

  const tabs = [['action', 'Needs your decision', counts.needs_action], ['patient', 'Waiting on patient', counts.waiting_on_patient], ['all', 'All', counts.all]]

  return (
    <DashboardLayout>
      <Head title="Finance applications" />
      <div className="mx-auto max-w-6xl space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-brand-primary"><Wallet size={18} /><span className="text-xs font-semibold uppercase tracking-wider">{lender.legal_name} · {lender.ncrcp}</span></div>
            <h1 className="mt-1 text-2xl font-semibold text-brand-ink">Finance applications</h1>
            <p className="mt-1 text-sm text-brand-muted">Patients of Dr Chalita le Roux Inc who applied to spread the cost of their treatment. Open one to review the documents and record your decision.</p>
          </div>
          {is_admin && <span className="rounded-full border border-brand-border bg-white px-3 py-1 text-xs font-semibold text-brand-muted">Practice owner view</span>}
        </div>

        <div className="flex flex-wrap gap-2">
          {tabs.map(([k, label, n]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
                    className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${tab === k ? 'border-brand-primary bg-brand-primary text-white' : 'border-brand-border bg-white text-brand-ink hover:bg-brand-surface'}`}>
              {label} <span className={`ml-1 tabular-nums ${tab === k ? 'text-white/80' : 'text-brand-muted'}`}>{n || 0}</span>
            </button>
          ))}
        </div>

        {list.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-brand-border bg-white p-10 text-center">
            <Inbox size={28} className="mx-auto text-brand-muted" />
            <p className="mt-3 font-semibold text-brand-ink">{tab === 'action' ? 'Nothing waiting for a decision' : 'No applications yet'}</p>
            <p className="mt-1 text-sm text-brand-muted">New applications appear here as soon as a patient sends one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-brand-border bg-white shadow-sm">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-brand-border text-left text-xs uppercase tracking-wide text-brand-muted">
                  <th className="px-4 py-3">Reference</th><th className="px-4 py-3">Applicant</th><th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Term</th><th className="px-4 py-3">Sent</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Documents</th><th className="px-2 py-3" />
                </tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr key={a.id} onClick={() => router.visit(`/finance-review/${a.id}`)} className="cursor-pointer border-b border-brand-border/70 last:border-0 hover:bg-brand-surface/60">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-brand-ink">{a.reference}</td>
                    <td className="px-4 py-3 text-brand-ink">{a.applicant}</td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums text-brand-ink">{a.amount}</td>
                    <td className="px-4 py-3 tabular-nums">{a.term} months</td>
                    <td className="px-4 py-3 text-brand-muted">
                      {when(a.submitted_at)}
                      {a.waiting_minutes != null && (
                        <span className={`ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${a.waiting_minutes > 24 * 60 ? 'bg-red-50 text-red-700' : 'bg-brand-surface text-brand-muted'}`}>
                          <Clock size={11} /> {waited(a.waiting_minutes)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3"><span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[a.status] || ''}`}>{a.status_label}</span></td>
                    <td className="px-4 py-3 text-brand-muted"><span className="inline-flex items-center gap-1"><FileText size={13} /> {a.purged ? 'Deleted' : a.documents}</span></td>
                    <td className="px-2 py-3 text-brand-muted"><ChevronRight size={16} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-brand-muted">Every view, download and decision is recorded. The practice deletes its copy of the documents 30 days after your decision, so download the full pack as your record.</p>
      </div>
    </DashboardLayout>
  )
}
